import React, { useState, useContext } from 'react';
import Dialog from 'Components/DesignSystem/Dialog/Dialog';
import Button from 'Components/DesignSystem/Button/Button';
import TextField from 'Components/DesignSystem/TextField/TextField';
import Typography from 'Components/DesignSystem/Typography/Typography';
import InfoTooltip from 'Components/DesignSystem/Tooltip/InfoTooltip';
import UploadImage from './UploadImage';
import { createUIModelApi, copyUIModelApi, updateUIModelApi } from '../../services/ApiHelper';
import { UIModelAPI, CompositionMode } from '../../types/api.types';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import Radio from '../../../../../../Components/DesignSystem/Radio/Radio';
import { useModule } from '../../contexts/ModuleContext';
import { useUIModelState } from '../../contexts/UIModelStateContext';
import { AppContext } from '../../../../../../Context/AppContext';

type CreateUIModelProps = {
  id?: string;
  projectId: string;
  uiModel?: UIModelAPI | null;
  onClose: () => void;
  onSuccess: (id: string) => void;
  isCopyModel?: boolean;
  /** Dialog title. Defaults from the mode: Create / Copy / Update UI Model. */
  title?: string;
};

/**
 * The Create / Copy / Update UI Model dialog (§ 102). Owns its DS Dialog
 * shell — the local `PopUp`/`PopUpHeader` wrappers retired — so consumers
 * render `<CreateUIModel …/>` directly and gain Escape / scrim / X dismissal
 * (the legacy fixed div had none; Cancel was the only way out). The create /
 * copy / update flows are untouched.
 *
 * The upload area stays hand-rolled FOR NOW — DS `FileUpload` (§ 214) exists
 * since 2026-09-04, and the swap is its own integration pass (same deviation
 * as UploadModal, § 98).
 */
const CreateUIModel = ({
  id,
  projectId,
  uiModel,
  onClose,
  onSuccess,
  isCopyModel,
  title,
}: CreateUIModelProps) => {
  const baseId = id || 'create-ui-model';

  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState(`${isCopyModel ? 'Copy of ' : ''}${uiModel?.name ?? ''}`);
  const [error, setError] = useState<string | null>(null);
  const [imageRemoved, setImageRemoved] = useState<boolean>(false);
  // Inherit the source model's mode (edit + copy); new models default to restricted.
  const [compositionMode, setCompositionMode] = useState<CompositionMode>(
    uiModel?.composition_mode === 'freestyle' ? 'freestyle' : 'restricted'
  );
  // Builder Mode is set at creation only — locked on edit AND copy (a copy's
  // layout was authored under the source model's mode).
  const isModeLocked = uiModel != null;
  const { setActiveView, submodelInstanceLimits, getSubmodelName } = useModule();
  // Freestyle is unavailable when any submodel in the data model allows
  // multiple instances (Max Instances > 1).
  const multiInstanceSubmodelNames = Array.from(submodelInstanceLimits.entries())
    .filter(([, limits]) => limits.maxInstantiation > 1)
    .map(([id]) => getSubmodelName(id) ?? id);
  const anyMultiInstance = multiInstanceSubmodelNames.length > 0;
  const { dispatch } = useUIModelState();
  const appContext = useContext(AppContext);
  const channelId = appContext?.channelKey ?? '';

  const dialogTitle =
    title ?? (uiModel == null ? 'Create UI Model' : isCopyModel ? 'Copy UI Model' : 'Update UI Model');

  const createUIModel = async (
    modelName: string,
    imageFile: File | string | null
  ): Promise<UIModelAPI> => {
    const formData = new FormData();
    if (imageFile != null && imageFile instanceof File) {
      formData.append('launchIcon', imageFile, imageFile.name);
    }
    if (imageFile != null && typeof imageFile === 'string') {
      formData.append('launchIcon', imageFile);
    }
    formData.append('channel_id', channelId);
    formData.append('name', modelName);
    formData.append('composition_mode', compositionMode);

    const response = await createUIModelApi(projectId, formData);
    if (response?.done && response.data?.model) {
      // Keep the chosen mode even if the API response doesn't echo it yet —
      // otherwise the editor session would silently fall back to restricted.
      const model: UIModelAPI = {
        ...response.data.model,
        composition_mode: response.data.model.composition_mode ?? compositionMode,
      };
      dispatch({ type: 'SET_MODEL', payload: model });
      setActiveView('editor');
      toast.success('Successfully created UI Model');
      return model;
    }
    throw new Error('Failed to create model');
  };

  const copyModel = async (
    modelName: string,
    imageFile: File | string | null,
    modelId: string
  ): Promise<UIModelAPI> => {
    const formData = new FormData();
    if (imageFile != null && imageFile instanceof File) {
      formData.append('launchIcon', imageFile, imageFile.name);
    }
    if (imageFile != null && typeof imageFile === 'string') {
      formData.append('launchIcon', imageFile);
    }
    formData.append('channel_id', channelId);
    formData.append('name', modelName);
    formData.append('composition_mode', compositionMode);

    const response = await copyUIModelApi(projectId, modelId, formData);
    if (response?.done && response.data?.model) {
      // Keep the chosen mode even if the API response doesn't echo it yet.
      const model: UIModelAPI = {
        ...response.data.model,
        composition_mode: response.data.model.composition_mode ?? compositionMode,
      };
      dispatch({ type: 'SET_MODEL', payload: model });
      setActiveView('editor');
      toast.success('Successfully copied UI Model');
      return model;
    }
    throw new Error('Failed to copy model');
  };

  const updateModel = async (
    modelId: string,
    modelName: string,
    imageFile: File | string | null
  ): Promise<UIModelAPI> => {
    const formData = new FormData();
    if (imageFile != null && imageFile instanceof File) {
      formData.append('launchIcon', imageFile, imageFile.name);
    }
    if (imageFile != null && typeof imageFile === 'string') {
      formData.append('launchIcon', imageFile);
    }
    formData.append('channel_id', channelId);
    formData.append('name', modelName);
    formData.append('composition_mode', compositionMode);

    const response = await updateUIModelApi(projectId, modelId, formData);
    if (response?.done && response.data?.model) {
      // Keep the chosen mode even if the API response doesn't echo it yet.
      const model: UIModelAPI = {
        ...response.data.model,
        composition_mode: response.data.model.composition_mode ?? compositionMode,
      };
      dispatch({ type: 'SET_MODEL', payload: model });
      toast.success('Successfully updated UI Model');
      return model;
    }
    throw new Error('Failed to update model');
  };

  const handleSave = async () => {
    if (loading) return;
    setError(null);
    if (name.length <= 0) {
      setError('Enter name');
      return;
    }
    setLoading(true);
    const imageFile =
      file == null ? (!imageRemoved ? uiModel?.launchIcon ?? '' : '') : file;
    try {
      if (uiModel == null) {
        await createUIModel(name, file);
      } else {
        if (isCopyModel === true) {
          await copyModel(name, imageFile, uiModel?.id);
        } else {
          await updateModel(uiModel?.id ?? '', name, imageFile);
        }
      }
      setLoading(false);
      onSuccess('');
    } catch (e: any) {
      toast.error(e.message ?? 'Error');
      setLoading(false);
      console.log(e);
    }
  };

  const saveLabel = uiModel == null ? 'Create' : isCopyModel ? 'Save Copy' : 'Update';
  const savingLabel = uiModel == null ? 'Creating…' : isCopyModel ? 'Copying…' : 'Updating…';

  /*
      While the save is in flight the dialog is COMMITTED: the API call is not
      abortable, and a mid-flight close only hides the dialog — the success
      toast, SET_MODEL dispatch and view switch still land afterwards. One
      guard covers every close route (Cancel, the X, Escape — Dialog routes
      all three through `onClose`); `handleSave` resets `loading` on success
      AND failure, so this can never wedge shut.
  */
  const guardedClose = () => {
    if (loading) return;
    onClose();
  };

  return (
    <Dialog
      isOpen
      onClose={guardedClose}
      title={dialogTitle}
      size="sm"
      data-testid={baseId}
      footer={
        <div className="tw-flex tw-w-full tw-justify-end tw-items-center tw-gap-3">
          <Button
            variant="secondary"
            id={`${baseId}-cancel-btn`}
            data-testid={`${baseId}-cancel-btn`}
            disabled={loading}
            onClick={guardedClose}
          >
            Cancel
          </Button>
          {/* § 209 — the DS loading state replaces the hand-assembled cursor-wait +
              inline-Spinner span. `handleSave` already no-ops while loading, so the
              disabled attribute this adds changes nothing a click could reach. */}
          <Button
            variant="primary"
            id={`${baseId}-save-btn`}
            data-testid={`${baseId}-save-btn`}
            disabled={name.length === 0}
            loading={loading}
            onClick={handleSave}
          >
            {loading ? savingLabel : saveLabel}
          </Button>
        </div>
      }
    >
      <div
        id={baseId}
        className="tw-space-y-[16px]"
      >
        <TextField
          label="UI Model Name"
          id={`${baseId}-name-input`}
          data-testid={`${baseId}-name-input`}
          placeholder="Enter Name"
          value={name}
          error={error != null}
          helperText={error ?? undefined}
          onChange={(value: string) => setName(value)}
        />
        <div id={`${baseId}-mode-section`} className="tw-space-y-[6px]">
          <Typography variant="sub-title-label" as="div" id={`${baseId}-mode-label`} className="tw-text-ink-secondary">
            Builder Mode
          </Typography>
          <p id={`${baseId}-mode-subtext`} className="tw-text-xs tw-italic tw-text-ink-secondary">
            Set at creation only — cannot be changed once the UI Model is created.
          </p>
          <div className="tw-space-y-2 tw-pt-1">
            <div className="tw-flex tw-items-center tw-gap-2">
              <Radio
                name={`${baseId}-composition-mode`}
                label="Structured"
                value="restricted"
                checked={compositionMode === 'restricted'}
                disabled={isModeLocked}
                data-testid={`${baseId}-mode-structured`}
                onChange={() => setCompositionMode('restricted')}
              />
              <span className="tw-text-xs tw-italic tw-text-ink-secondary">(recommended)</span>
              <InfoTooltip
                placement="bottom"
                id={`${baseId}-mode-structured-info`}
                data-testid={`${baseId}-mode-structured-info`}
                message={
                  <span className="tw-block tw-w-64 tw-whitespace-normal tw-text-left">
                    <span className="tw-block tw-font-semibold">Structured (recommended)</span>
                    <span className="tw-block">
                      Each container holds fields from only one submodel. Once a field is used
                      in a container, it&apos;s locked there and can&apos;t be reused elsewhere.
                    </span>
                  </span>
                }
              />
            </div>
            <div className="tw-flex tw-items-center tw-gap-2">
              <Radio
                name={`${baseId}-composition-mode`}
                label="Freestyle"
                value="freestyle"
                checked={compositionMode === 'freestyle'}
                disabled={isModeLocked || anyMultiInstance}
                data-testid={`${baseId}-mode-freestyle`}
                onChange={() => setCompositionMode('freestyle')}
              />
              <span className="tw-text-xs tw-italic tw-text-ink-secondary">(advanced)</span>
              <InfoTooltip
                placement="bottom"
                id={`${baseId}-mode-freestyle-info`}
                data-testid={`${baseId}-mode-freestyle-info`}
                message={
                  <span className="tw-block tw-w-64 tw-whitespace-normal tw-text-left">
                    <span className="tw-block tw-font-semibold">Freestyle (advanced)</span>
                    <span className="tw-block">
                      Drag any submodel&apos;s fields into any container, including fields
                      already used elsewhere or from other submodels. No guardrails.
                    </span>
                    <span className="tw-block tw-font-semibold tw-text-tone-danger-ink tw-mt-1">
                      {anyMultiInstance
                        ? `Unavailable: ${multiInstanceSubmodelNames.join(', ')} ${multiInstanceSubmodelNames.length === 1 ? 'has' : 'have'} Max Instances > 1. Reduce to 1 to enable Freestyle.`
                        : 'Unavailable if any submodel allows multiple instances (Max Instances > 1).'}
                    </span>
                  </span>
                }
              />
            </div>
            {!isModeLocked && anyMultiInstance && (
              <p id={`${baseId}-mode-gate-note`} className="tw-text-xs tw-text-ink-secondary">
                Freestyle is unavailable —{' '}
                {multiInstanceSubmodelNames.join(', ')}{' '}
                {multiInstanceSubmodelNames.length === 1 ? 'has' : 'have'} Max Instances &gt; 1.
                Reduce to 1 to enable Freestyle.
              </p>
            )}
          </div>
        </div>
        <div id={`${baseId}-image-section`} className="tw-space-y-[10px]">
          <Typography variant="sub-title-label" as="div" id={`${baseId}-image-label`} className="tw-text-ink-secondary">
            Add Launch Image
          </Typography>
          <UploadImage
            id={`${baseId}-image-upload`}
            setFileParent={setFile}
            launchIcon={!imageRemoved ? uiModel?.launchIcon : undefined}
            onRemovePressed={() => {
              if (uiModel != null && !imageRemoved) {
                setImageRemoved(true);
              }
            }}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default CreateUIModel;
